'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '../lib/supabase/client';
import { User, Session } from '@supabase/supabase-js';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, metadata?: SignUpMetadata) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<void>;
  getCurrentUser: () => Promise<User | null>;
  isEmailVerified: () => boolean;
  getUserProfile: () => Promise<any>;
  getUserPlan: () => Promise<'Basic' | 'Luxe' | 'Pro' | 'Enterprise'>;
}

interface SignUpMetadata {
  fullName?: string;
  agentId?: string;
  carrierAffiliation?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const CONFIG_ERROR = 'Supabase is not configured. Add NEXT_PUBLIC_SUPABASE_URL and NEXT_PUBLIC_SUPABASE_ANON_KEY to .env and restart the dev server.';

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const url = typeof process.env.NEXT_PUBLIC_SUPABASE_URL === 'string' ? process.env.NEXT_PUBLIC_SUPABASE_URL : '';
  const key = typeof process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY === 'string' ? process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY : '';
  const hasConfig = url.length > 0 && key.length > 0;

  if (!hasConfig) {
    return (
      <AuthContext.Provider
        value={{
          user: null,
          session: null,
          loading: false,
          signUp: async () => {
            throw new Error(CONFIG_ERROR);
          },
          signIn: async () => {
            throw new Error(CONFIG_ERROR);
          },
          signOut: async () => {},
          getCurrentUser: async () => null,
          isEmailVerified: () => false,
          getUserProfile: async () => null,
          getUserPlan: async () => 'Basic',
        }}
      >
        {children}
      </AuthContext.Provider>
    );
  }

  const supabase = createClient();

  useEffect(() => {
    let cancelled = false;
    // Fallback: unblock UI after 1.2s so dashboard doesn't spin forever if getSession hangs
    const fallbackTimeout = setTimeout(() => {
      if (!cancelled) setLoading(false);
    }, 1200);

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (cancelled) return;
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });

    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (cancelled) return;
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => {
      cancelled = true;
      clearTimeout(fallbackTimeout);
      subscription.unsubscribe();
    };
  }, []);

  // Identity propagation: sync profile_picture from auth metadata to user_profiles on every session start
  // so Header/Sidebar have a URL and the initials fallback is avoided when provider supplies an avatar
  useEffect(() => {
    if (!session?.user?.id) return;
    const url =
      (session.user.user_metadata?.avatar_url as string)?.trim() ||
      (session.user.user_metadata?.picture as string)?.trim();
    if (!url) return;
    supabase
      .from('user_profiles')
      .update({ profile_picture_url: url })
      .eq('id', session.user.id)
      .then(({ error }) => {
        if (error) console.error('Profile picture sync from metadata:', error);
      });
  }, [session?.user?.id, session?.user?.user_metadata?.avatar_url, session?.user?.user_metadata?.picture, supabase]);

  // Email/Password Sign Up with agent metadata
  const signUp = async (email: string, password: string, metadata: SignUpMetadata = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.fullName || '',
          agent_id: metadata?.agentId || '',
          carrier_affiliation: metadata?.carrierAffiliation || ''
        },
        emailRedirectTo: `${window.location.origin}/main-commission-dashboard`
      }
    });
    if (error) throw error;
    
    // If user is created, update user_profiles with agent_id and carrier_affiliation (no trial)
    if (data.user) {
      const { error: profileError } = await supabase
        .from('user_profiles')
        .update({
          agent_id: metadata?.agentId || '',
          carrier_affiliation: metadata?.carrierAffiliation || ''
        })
        .eq('id', data.user.id);

      if (profileError) {
        console.error('Error updating profile:', profileError);
      }
    }
    
    return data;
  };

  // Email/Password Sign In
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (error) throw error;
    return data;
  };

  // Sign Out, always redirect to ID/Password sign-in page
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    window.location.href = '/login-authentication';
  };

  // Get Current User
  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  // Check if Email is Verified
  const isEmailVerified = () => {
    return user?.email_confirmed_at !== null;
  };

  // Get User Profile from Database (includes profile_picture_url, plan_id for identity & plan logic)
  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (error) throw error;
    return data;
  };

  // Get User Plan from DB. Override and founder legacy grant Luxe. Fallback Basic on error.
  const getUserPlan = async (): Promise<'Basic' | 'Luxe' | 'Pro' | 'Enterprise'> => {
    if (!user) return 'Basic';
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('plan_id, luxe_override, free_until_date, lifetime_access, founder_legacy_until')
        .eq('id', user.id)
        .single();
      if (error || !data) return 'Basic';
      const hasFounderLegacy =
        !!data.founder_legacy_until && new Date(data.founder_legacy_until) > new Date();
      const hasOverride = !!(
        hasFounderLegacy ||
        data.luxe_override ||
        data.lifetime_access ||
        (data.free_until_date && new Date(data.free_until_date) > new Date())
      );
      const raw = (data.plan_id as 'Basic' | 'Luxe' | 'Pro' | 'Enterprise') || 'Basic';
      return hasOverride ? 'Luxe' : raw;
    } catch {
      return 'Basic';
    }
  };

  // Force loading to false after 300ms so no page gets stuck on "Syncing Ledger..." or blank
  const [forceUnblock, setForceUnblock] = useState(false);
  
  useEffect(() => {
    const timeout = setTimeout(() => {
      setForceUnblock(true);
    }, 300);
    return () => clearTimeout(timeout);
  }, []);

  const value = {
    user,
    session,
    loading: forceUnblock ? false : loading, // After 300ms, always false so UI never blocks
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    getUserPlan
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
